import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAppearanceChanges } from '../../hooks/appearance-changes';
import { unreachable } from '../../hooks/request';
import { useSession } from '../session';

const AppearanceSavesContext = createContext();

// The account's appearance (C27) and whether its latest choice is saved yet
// (#136). Choosing takes effect on the radios at once and is saved without a
// button, and the person can leave Settings and come back before a save has
// settled, so the saves live above every screen rather than in the switch on
// screen: one writer for the account, however often Settings opens.
//
// One write goes at a time and the last one is always the latest choice: a
// choice made while a write is out waits, choices in between that were never
// sent are dropped, and a write once sent is never taken back, since it may
// already have been applied. The page is drawn in the appearance the service
// last acknowledged, read from the write's own answer, so there is no second
// read whose failure could make a saved choice look refused.
//
// What the service holds is known only from its answers (#122). A choice it
// refused was not applied: the switch goes back to the stored appearance and
// says why. A write whose answer never arrived may well have been applied:
// the stored appearance becomes uncertain, the choice stays on the switch
// with the reason and Try again, and it is sent again, even when it is the
// appearance stored before.
//
// It lives for one signed-in account (components/account-lifetime): when
// that account's session ends, nothing more is sent and a late answer
// changes nothing, and each write names its account, which the service
// checks, for the session that changes hands in another tab.
export function AppearanceSavesProvider({ children }) {
  const { me } = useSession();
  const saveAppearance = useAppearanceChanges();
  const [ version, setVersion ] = useState(0);
  const state = useRef(null);

  if (state.current === null) {
    let owner = null;
    let stored;

    if (me != null) {
      owner = me.id;
      stored = me.profile.appearance;
    }

    state.current = { owner, stored, latest: undefined, certain: true, sending: false, failure: undefined, retired: false };
  }

  useEffect(function() {
    const current = state.current;

    return function() {
      current.retired = true;
    };
  }, []);

  const changed = useCallback(function() {
    setVersion(function(was) {
      return was + 1;
    });
  }, []);

  const pump = useCallback(function() {
    const current = state.current;
    const appearance = current.latest;

    if (current.retired === true || current.sending === true || current.failure != null || appearance === undefined) {
      return;
    }

    if (current.certain === true && appearance === current.stored) {
      return;
    }

    current.sending = true;
    changed();

    saveAppearance({ appearance, expectedAccount: current.owner }).then(function(stored) {
      current.stored = stored;
      current.certain = true;
    }).catch(function(failed) {
      if (failed.message === unreachable) {
        current.certain = false;
      }

      // A failure belongs to the choice it tried to save: when a newer one
      // has been made since, that one is simply sent next.
      if (current.latest === appearance) {
        current.failure = failed.message;

        if (current.certain === true) {
          current.latest = undefined;
        }
      }
    }).then(function() {
      current.sending = false;

      if (current.retired === false) {
        changed();
        pump();
      }
    });
  }, [ saveAppearance, changed ]);

  const choose = useCallback(function(appearance) {
    state.current.latest = appearance;
    state.current.failure = undefined;
    changed();
    pump();
  }, [ changed, pump ]);

  const retry = useCallback(function() {
    state.current.failure = undefined;
    changed();
    pump();
  }, [ changed, pump ]);

  const { stored, latest, certain, failure } = state.current;
  let chosen = stored;

  if (latest !== undefined) {
    chosen = latest;
  }

  const value = useMemo(function() {
    return { version, chosen, applied: stored, failure, uncertain: certain === false, choose, retry };
  }, [ version, chosen, stored, failure, certain, choose, retry ]);

  return <AppearanceSavesContext.Provider value={ value }>{ children }</AppearanceSavesContext.Provider>;
}

export function useAppearanceSaves() {
  return useContext(AppearanceSavesContext);
}
